import { API_ENDPOINT } from '@Env/variables';
import { createAxiosInstance } from './axios';
import axiosLib, { isAxiosError } from 'axios';
import {
  JiraProject,
  JiraIssue,
  JiraSavedProjectResponse,
  JiraVersion,
  JiraAssignedTicketsResponse,
  JiraAssignedTicket,
} from '@Models/types';

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const jiraAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, API_ENDPOINT);

  return {
    getVersions: async (projectKey: string) => {
      const res = await axios.get<JiraVersion[]>('/api/getVersions', {
        params: { projectKey },
      });
      return res.data;
    },

    getProjects: async () => {
      const res = await axios.get<{ projects: JiraProject[] }>(
        '/api/jira/getProjects'
      );
      return res.data.projects;
    },

    getFavProjects: async () => {
      const res = await axios.get<{ FavProjects: JiraProject[] }>(
        '/api/jira/getFavProjects'
      );
      return res.data.FavProjects;
    },

    saveFavProjects: async (favoriteProjectIds: JiraProject[]) => {
      const res = await axios.post<JiraSavedProjectResponse>(
        '/api/jira/setFavProjects',
        {
          favoriteProjectIds,
        }
      );
      return res.data.favorites;
    },

    getIssueList: async (
      projectKey: string,
      pageToken?: string,
      search?: string
    ) => {
      try {
        const res: { data: { issues: JiraIssue[]; nextPageToken?: string } } =
          await axios.get('/api/jira/getIssueList', {
            params: {
              project_key: projectKey,
              nextPageToken: pageToken,
              search: search || undefined, //axios omits the entire query param in the url if undefined
            },
          });

        return {
          issues: res.data.issues,
          nextPageToken: res.data.nextPageToken,
        };
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 429) {
          const rawRetryAfter = error.response.data?.retryAfter;
          const parsedRetryAfter = Number(rawRetryAfter);
          const retrySecs =
            Number.isFinite(parsedRetryAfter) && parsedRetryAfter > 0
              ? parsedRetryAfter
              : 5;
          console.warn(`Jira rate limit hit. Waiting ${retrySecs}s...`);
          await delay(retrySecs * 1000);
        }
        throw error;
      }
    },

    getIssueDetails: async (issueKey: string) => {
      const res = await axios.get<JiraIssue>('/api/jira/getIssueDetails', {
        params: { issue_key: issueKey },
      });
      return res.data;
    },

    getAssignedRecords: async () => {
      const email = localStorage.getItem('email') || '';
      const res = await axios.get<JiraAssignedTicketsResponse>(
        `/api/getJiraAssignedRecords?email=${email}`
      );

      return res.data.assigned.map((record: JiraAssignedTicket) => ({
        reference_num: record.reference_num,
        name: record.name,
        status: record.status,
        workflow_status: record.workflow_status
          ? {
              id: record.workflow_status.id,
              name: record.workflow_status.name,
              position: record.workflow_status.position,
              complete: record.workflow_status.complete,
              color: record.workflow_status.color,
            }
          : undefined,
        record_type: record.record_type,
        id: record.id,
        priority: record.priority || 'No priority set',
        url: record.url,
        attachment: record.attachment,
        isDetailLoaded: false,
      })) as JiraAssignedTicket[];
    },

    getAssignedTicketDetails: async (
      issueKey: string,
      signal?: AbortSignal
    ) => {
      const email = localStorage.getItem('email') || '';

      // Bridge AbortSignal → axios CancelToken so cancellation works with
      // the existing axios instance that doesn't use the native fetch adapter.
      const source = axiosLib.CancelToken.source();
      if (signal) {
        signal.addEventListener('abort', () => source.cancel('AbortError'));
      }

      const res = await axios.get<{ ticket: JiraAssignedTicket }>(
        `/api/getJiraAssignedRecords`,
        {
          params: { email, issueKey },
          cancelToken: source.token,
        }
      );

      return res.data.ticket;
    },
  };
};
